import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Wrench, Megaphone, Calendar, CheckCircle2, ArrowRight, Phone, Sparkles } from 'lucide-react';

const SAMSolutions = () => {
  const services = [
    {
      icon: <Home className="w-12 h-12 text-[#5DADE2]" />,
      title: 'Family Relocation Support',
      description: 'Complete project management for your family move, from house hunting to school enrollment and everything in between.',
      features: ['Housing search', 'School placement', 'Utilities setup', 'Moving coordination'],
    },
    {
      icon: <Wrench className="w-12 h-12 text-[#3498DB]" />,
      title: 'Fit-Out & Maintenance',
      description: 'Professional interior design, renovation, and ongoing maintenance services for your home or office.',
      features: ['Interior design', 'Renovation projects', 'Handyman services', 'Regular maintenance'],
    },
    {
      icon: <Megaphone className="w-12 h-12 text-[#27AE60]" />,
      title: 'Marketing Solutions',
      description: 'Comprehensive marketing services to boost your brand visibility and business growth in Dubai.',
      features: ['Digital marketing', 'Brand strategy', 'Content creation', 'Social media management'],
    },
    {
      icon: <Calendar className="w-12 h-12 text-[#5DADE2]" />,
      title: 'Events & Activities',
      description: 'Organization of corporate events, team building activities, and extracurricular programs for all ages.',
      features: ['Corporate events', 'Team building', 'Kids activities', 'Special occasions'],
    },
  ];

  const benefits = [
    'Single point of contact for all your needs',
    'Coordinated project management across services',
    'Time-saving one-stop solution',
    'Trusted network of service providers',
    'Quality control and satisfaction guarantee',
    'Flexible packages tailored to your requirements',
    'Cost-effective bundled services',
    'Dedicated account manager',
  ];

  return (
    <div className="min-h-screen" data-testid="sam-solutions-page">
      {/* Hero Section */}
      <section
        className="relative h-[60vh] flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="solutions-hero"
      >
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block bg-[#5DADE2]/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-[#5DADE2] font-semibold">SAM Solutions</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="solutions-hero-title">
              Your <span className="text-[#5DADE2]">One-Stop Solution</span> in Dubai
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
              Comprehensive project management support for families and businesses in the UAE
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="overview-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-semibold mb-6 text-[#2C3E50]">
                What is SAM Solutions?
              </h2>
              <div className="gold-line mb-6" />
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  SAM Solutions is your comprehensive service provider for all aspects of life and business in Dubai. We act as your single point of contact, coordinating multiple services to save you time and hassle.
                </p>
                <p>
                  Whether you're relocating your family, setting up an office, planning a corporate event, or need ongoing maintenance support, SAM Solutions provides end-to-end project management with quality assurance.
                </p>
                <p>
                  Our approach is simple: tell us what you need, and we'll handle everything from planning to execution, ensuring seamless coordination across all service providers.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800"
                alt="Solutions Services"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#5DADE2] rounded-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-br from-[#F4F4F5] to-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-semibold mb-4 text-[#2C3E50]">
              Our Service Portfolio
            </h2>
            <div className="gold-line mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="service-card"
                data-testid={`service-${index}`}
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-2xl font-semibold mb-4 text-[#2C3E50]">{service.title}</h3>
                <p className="text-gray-700 leading-relaxed mb-4">{service.description}</p>
                <div className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-[#5DADE2]" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-semibold mb-4 text-[#2C3E50]">
              Why Choose SAM Solutions?
            </h2>
            <div className="gold-line mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="flex items-start gap-4 p-6 bg-gradient-to-br from-white to-[#F4F4F5] rounded-xl border border-gray-200 hover:border-[#5DADE2]/50 transition-all"
                data-testid={`benefit-${index}`}
              >
                <CheckCircle2 className="w-6 h-6 text-[#27AE60] flex-shrink-0 mt-1" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#3498DB] to-[#5DADE2] text-white" data-testid="solutions-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold mb-6">
              Simplify Your Life with SAM Solutions
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Let us handle the details while you focus on what matters most. Contact us for a customized solution package.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-gold ripple inline-flex items-center justify-center gap-2">
                Get Started
                <ArrowRight size={18} />
              </Link>
              <a
                href="tel:+971563528126"
                className="bg-white text-[#3498DB] hover:bg-gray-100 px-8 py-4 rounded-xl font-semibold transition-all hover:scale-105 inline-flex items-center justify-center gap-2 ripple"
              >
                <Phone size={18} />
                Call Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SAMSolutions;